import type { NavigateFunction } from 'react-router-dom';
import { BROWSER_ROUTES } from '@constants/browser.routes';
import {
	ATTACHMENT_OPTION_CONFIGURATION_PERMISSIONS,
	ATTACHMENT_TYPE_CONFIGURATION_PERMISSIONS,
	CONFIGURATION_SECTION_PERMISSIONS,
	COURSE_CONTENT_CONFIGURATION_PERMISSIONS,
	COURSE_STREAM_CONFIGURATION_PERMISSIONS,
	EXECUTIVE_DASHBOARD_PERMISSIONS,
	INSTITUTE_FACILITY_CONFIGURATION_PERMISSIONS,
	ORGANIZATION_CONFIGURATION_PERMISSIONS,
	PERMISSIONS,
	QUALIFICATION_CONFIGURATION_PERMISSIONS,
	COMPETENCY_POINT_CONFIGURATION_PERMISSIONS,
	SECTOR_CONFIGURATION_PERMISSIONS,
	COURSE_SCHEME_CONFIGURATION_PERMISSIONS,
} from '@constants/user.roles';
import { permissionListIncludes } from '@utils/permissionIdentifiers';
import {
	type PsdaAdminDashboardAccessContext,
	permissionListIncludesPsdaAdminDashboard,
} from '@utils/psdaAdminDashboardAccess';
import {
	EVENT_ENTITY_PSDA,
	EVENT_ENTITY_PVTC,
	EVENT_ENTITY_TEVTA,
	resolveEventEntity,
} from '@features/EventManagement/utils/eventEntity';
import {
	EVENT_MANAGEMENT_ORDERED_ROUTE_PERMISSIONS,
	EVENT_MANAGEMENT_PATH_PERMISSION_ENTRIES,
	resolveEventManagementFirstAllowedRoute,
} from '@features/EventManagement/integration/navigation';
import {
	STATIC_COURSE_ASSIGN_ORDERED_ROUTE_PERMISSIONS,
	STATIC_COURSE_ASSIGN_PATH_PERMISSION_ENTRIES,
	resolveStaticCourseAssignFirstAllowedRoute,
} from '@features/CourseAssign/integration/navigation';
import { IS_REPORT_MODULE_NAV_ENABLED } from '@features/ReportModule/reportModule.flags';

/**
 * Path-to-permission mapping for route guarding.
 * Order matters: more specific paths should come before prefixes (e.g. /applications/scrutiny before /applications).
 * Paths use :param for dynamic segments. Unknown paths not listed here are treated as not allowed.
 */
type PathPermissionEntry = {
	pattern: RegExp | string;
	permission: string;
	alternatePermissions?: readonly string[];
	/** When true, `all.read` does not grant route access (API requires explicit permission). */
	requireExplicitPermission?: boolean;
};

const PATH_PERMISSION_ENTRIES: PathPermissionEntry[] = [
	{ pattern: '/institutes/unauthorized-institutes', permission: PERMISSIONS.UNAUTHORIZED_INSTITUTE_LIST, alternatePermissions: [PERMISSIONS.UNAUTHORIZED_INSTITUTE_VIEW] },
	{
		pattern: '/',
		permission: PERMISSIONS.PSDA_ADMIN_DASHBOARD,
		alternatePermissions: [
			PERMISSIONS.CHAIRMAN_PBTE_DASHBOARD,
			...EXECUTIVE_DASHBOARD_PERMISSIONS,
		],
	},
	{ pattern: '/institutes', permission: PERMISSIONS.INSTITUTES_LIST, alternatePermissions: [PERMISSIONS.INSTITUTES_VIEW] },
	{
		pattern: '/traceability',
		permission: PERMISSIONS.TRACEABILITY_LIST,
		alternatePermissions: [PERMISSIONS.TRACEABILITY_VIEW],
	},
	{ pattern: '/applications/scrutiny', permission: PERMISSIONS.APPLICATIONS_PENDING_LIST },
	{ pattern: '/applications/referred-back', permission: PERMISSIONS.APPLICATIONS_REFERRED_BACK_LIST },
	{ pattern: '/applications/scrutinized', permission: PERMISSIONS.APPLICATIONS_SCRUTINIZED_LIST },
	{
		pattern: '/applications/inspected',
		permission: PERMISSIONS.APPLICATIONS_INSPECTED_LIST,
		alternatePermissions: [
			PERMISSIONS.APPLICATIONS_VERIFIED_LIST,
			PERMISSIONS.APPLICATIONS_APPROVED_LIST,
			PERMISSIONS.ISSUE_CERTIFICATE,
		],
	},
	{
		pattern: /^\/applications\/pending-rejection/,
		permission: PERMISSIONS.APPLICATION_REJECTION_PENDING_LIST,
		alternatePermissions: [
			PERMISSIONS.APPLICATION_REJECTION_VIEW,
			PERMISSIONS.APPLICATION_REJECTION_APPROVED_LIST,
			PERMISSIONS.APPLICATION_REJECTION_REJECTED_LIST,
			PERMISSIONS.APPLICATION_REJECTION_CANCELLED_LIST,
		],
	},
	{
		pattern: '/appeals',
		permission: PERMISSIONS.APPLICATION_APPEAL_VIEW,
		alternatePermissions: [PERMISSIONS.APPLICATION_APPEAL_PENDING_LIST],
	},
	{
		pattern: '/committees/comittee',
		permission: PERMISSIONS.MY_COMMITTEE_PENDING_APPROVAL_LIST,
		alternatePermissions: [
			PERMISSIONS.MY_COMMITTEE_CONSTITUTED_LIST,
			PERMISSIONS.MY_COMMITTEE_APPROVED_LIST,
			PERMISSIONS.MY_COMMITTEE_REJECTED_LIST,
		],
	},
	{ pattern: '/committees/requests', permission: PERMISSIONS.REQUESTS_LISTS },
	{
		pattern: '/committees/history',
		permission: PERMISSIONS.HISTORY_CONSTITUTED_LIST,
		alternatePermissions: [PERMISSIONS.HISTORY_APPROVED_LIST, PERMISSIONS.HISTORY_REJECT_LIST],
	},
	{
		pattern: '/certificates',
		permission: PERMISSIONS.CERTIFICATES_VIEW,
		alternatePermissions: [PERMISSIONS.CERTIFICATES_LIST],
	},
	{ pattern: '/members', permission: PERMISSIONS.COMMITTEE_MEMBER_LIST_READ },
	{
		pattern: /^\/course-builder/,
		permission: PERMISSIONS.COURSES_LIST,
		alternatePermissions: [PERMISSIONS.LABS_LIST, PERMISSIONS.EQUIPMENTS_LIST],
	},
	{ pattern: '/calendar', permission: PERMISSIONS.ANNUAL_CALENDAR_VIEW, alternatePermissions: [PERMISSIONS.ANNUAL_CALENDAR_LIST] },
	...EVENT_MANAGEMENT_PATH_PERMISSION_ENTRIES,
	...STATIC_COURSE_ASSIGN_PATH_PERMISSION_ENTRIES,
	{
		pattern: '/manage-sessions',
		permission: PERMISSIONS.MANAGE_SESSIONS_LIST,
		alternatePermissions: [PERMISSIONS.MANAGE_SESSIONS_VIEW],
	},
	{ pattern: '/reports', permission: PERMISSIONS.REPORTS_VIEW },
	{
		pattern: '/employee-endorsement',
		permission: PERMISSIONS.EMPLOYER_LIST,
		requireExplicitPermission: true,
	},
	{ pattern: '/report-module', permission: PERMISSIONS.CHAIRMAN_TEVTA_DASHBOARD },
	{
		pattern: '/apprenticeship/statistics',
		permission: PERMISSIONS.APPRENTICESHIP_DASHBOARD_SUMMARY,
	},
	{
		pattern: '/apprenticeship/outcomes',
		permission: PERMISSIONS.APPRENTICESHIP_OUTCOMES_LIST,
		alternatePermissions: [
			PERMISSIONS.APPRENTICESHIP_OUTCOMES_VIEW,
			PERMISSIONS.APPRENTICESHIP_APPRENTICE_LIST,
			PERMISSIONS.APPRENTICESHIP_APPRENTICE_VIEW,
		],
	},
	{
		pattern: '/apprenticeship/apprentices',
		permission: PERMISSIONS.APPRENTICESHIP_APPRENTICE_LIST,
		alternatePermissions: [PERMISSIONS.APPRENTICESHIP_APPRENTICE_VIEW],
	},
	{
		pattern: '/apprenticeship/industry',
		permission: PERMISSIONS.APPRENTICESHIP_INDUSTRY_LIST,
		alternatePermissions: [PERMISSIONS.APPRENTICESHIP_INDUSTRY_VIEW],
	},
	{
		pattern: '/manage-users',
		permission: PERMISSIONS.MANAGE_USERS_LIST,
		alternatePermissions: [PERMISSIONS.MANAGE_USERS_VIEW],
	},
	{
		pattern: '/roles-and-permissions',
		permission: PERMISSIONS.ROLES_LIST,
		alternatePermissions: [PERMISSIONS.ROLES_VIEW],
	},
	{ pattern: '/audit-logs', permission: PERMISSIONS.AUDIT_LOGS_VIEW },
	{
		pattern: '/configuration',
		permission: CONFIGURATION_SECTION_PERMISSIONS[0],
		alternatePermissions: CONFIGURATION_SECTION_PERMISSIONS.slice(1),
	},
	{ pattern: /^\/faq/, permission: PERMISSIONS.FAQS_LIST, alternatePermissions: [PERMISSIONS.FAQS_VIEW] },
	{ pattern: '/institutes/unauthorized-institutes', permission: PERMISSIONS.UNAUTHORIZED_INSTITUTE_LIST, alternatePermissions: [PERMISSIONS.UNAUTHORIZED_INSTITUTE_VIEW] },
	{ pattern: /^\/verify-application\/[^/]+$/, permission: PERMISSIONS.APPLICATIONS_VIEW },
	{ pattern: /^\/director-inspection\/[^/]+$/, permission: PERMISSIONS.APPLICATIONS_INSPECTED_LIST },
	{ pattern: /^\/applications\/rejected$/, permission: PERMISSIONS.APPLICATIONS_VIEW },
	{ pattern: /^\/applications\/approved$/, permission: PERMISSIONS.APPLICATIONS_APPROVED_LIST },
];

/**
 * Ordered list of [path, permission] for resolving first allowed route. Same order as sidebar.
 */
const ORDERED_ROUTE_PERMISSIONS: [string, string][] = [
	[BROWSER_ROUTES.DASHBOARD, PERMISSIONS.PSDA_ADMIN_DASHBOARD],
	[BROWSER_ROUTES.INSTITUTES, PERMISSIONS.INSTITUTES_LIST],
	[BROWSER_ROUTES.TRACEABILITY, PERMISSIONS.TRACEABILITY_LIST],
	[BROWSER_ROUTES.APPLICATIONS_SCRUTINY, PERMISSIONS.APPLICATIONS_PENDING_LIST],
	[BROWSER_ROUTES.APPLICATIONS_REFERRED_BACK, PERMISSIONS.APPLICATIONS_REFERRED_BACK_LIST],
	[BROWSER_ROUTES.APPLICATIONS_SCRUTINIZED, PERMISSIONS.APPLICATIONS_SCRUTINIZED_LIST],
	[BROWSER_ROUTES.APPLICATIONS_INSPECTED, PERMISSIONS.APPLICATIONS_INSPECTED_LIST],
	[BROWSER_ROUTES.APPLICATIONS_PENDING_REJECTION, PERMISSIONS.APPLICATION_REJECTION_PENDING_LIST],
	[BROWSER_ROUTES.APPEALS, PERMISSIONS.APPLICATION_APPEAL_VIEW],
	[BROWSER_ROUTES.COMMITTEES_MY, PERMISSIONS.MY_COMMITTEE_PENDING_APPROVAL_LIST],
	[BROWSER_ROUTES.COMMITTEES_REQUESTS, PERMISSIONS.REQUESTS_LISTS],
	[BROWSER_ROUTES.COMMITTEES_HISTORY, PERMISSIONS.HISTORY_CONSTITUTED_LIST],
	[BROWSER_ROUTES.CERTIFICATES, PERMISSIONS.CERTIFICATES_VIEW],
	[BROWSER_ROUTES.MEMBERS, PERMISSIONS.COMMITTEE_MEMBER_LIST_READ],
	[BROWSER_ROUTES.COURSE_BUILDER, PERMISSIONS.COURSES_LIST],
	[BROWSER_ROUTES.CALENDAR, PERMISSIONS.ANNUAL_CALENDAR_LIST],
	...EVENT_MANAGEMENT_ORDERED_ROUTE_PERMISSIONS,
	...STATIC_COURSE_ASSIGN_ORDERED_ROUTE_PERMISSIONS,
	[BROWSER_ROUTES.MANAGE_SESSIONS, PERMISSIONS.MANAGE_SESSIONS_LIST],
	[BROWSER_ROUTES.REPORTS, PERMISSIONS.REPORTS_VIEW],
	[BROWSER_ROUTES.EMPLOYEE_ENDORSEMENT, PERMISSIONS.EMPLOYER_LIST],
	...(IS_REPORT_MODULE_NAV_ENABLED
		? ([[BROWSER_ROUTES.REPORT_MODULE, PERMISSIONS.CHAIRMAN_TEVTA_DASHBOARD]] as [string, string][])
		: []),
	[BROWSER_ROUTES.APPRENTICESHIP_STATISTICS, PERMISSIONS.APPRENTICESHIP_DASHBOARD_SUMMARY],
	[BROWSER_ROUTES.APPRENTICESHIP_OUTCOMES, PERMISSIONS.APPRENTICESHIP_OUTCOMES_LIST],
	[BROWSER_ROUTES.APPRENTICESHIP_APPRENTICES, PERMISSIONS.APPRENTICESHIP_APPRENTICE_LIST],
	[BROWSER_ROUTES.APPRENTICESHIP_INDUSTRY, PERMISSIONS.APPRENTICESHIP_INDUSTRY_LIST],
	[BROWSER_ROUTES.MANAGE_USERS, PERMISSIONS.MANAGE_USERS_LIST],
	[BROWSER_ROUTES.ROLES_AND_PERMISSIONS, PERMISSIONS.ROLES_LIST],
	[BROWSER_ROUTES.AUDIT_LOGS, PERMISSIONS.AUDIT_LOGS_VIEW],
	[BROWSER_ROUTES.CONFIGURATION, CONFIGURATION_SECTION_PERMISSIONS[0]],
	[BROWSER_ROUTES.FAQ_SECTIONS, PERMISSIONS.FAQS_LIST],
	[BROWSER_ROUTES.UNAUTHORIZED_INSTITUTE_REPORTS, PERMISSIONS.UNAUTHORIZED_INSTITUTE_LIST],
];

function pathMatches(pattern: RegExp | string, pathname: string): boolean {
	if (typeof pattern === 'string') {
		return pathname === pattern || pathname.startsWith(pattern + '/');
	}
	return pattern.test(pathname);
}

function findPathPermissionEntry(pathname: string): PathPermissionEntry | null {
	const normalized = pathname.replace(/\?.*$/, '');
	for (const entry of PATH_PERMISSION_ENTRIES) {
		if (pathMatches(entry.pattern, normalized)) return entry;
	}
	return null;
}

/** Routes where `all.read` must not substitute for the mapped API permission. */
export function pathRequiresExplicitPermission(pathname: string): boolean {
	return findPathPermissionEntry(pathname)?.requireExplicitPermission === true;
}

/**
 * Get the required permission for a path. Returns null only if path is not in the permission list (unknown path).
 * Unknown paths are not allowed — caller should redirect.
 */
export function getRequiredPermissionForPath(pathname: string): string | null {
	const normalized = pathname.replace(/\?.*$/, '');
	for (const { pattern, permission } of PATH_PERMISSION_ENTRIES) {
		if (pathMatches(pattern, normalized)) return permission;
	}
	return null;
}

/**
 * Get alternate permissions that also allow access to this path (if any).
 */
export function getAlternatePermissionForPath(pathname: string): string | null {
	const normalized = pathname.replace(/\?.*$/, '');
	for (const entry of PATH_PERMISSION_ENTRIES) {
		if (pathMatches(entry.pattern, normalized) && entry.alternatePermissions?.length) {
			return entry.alternatePermissions[0];
		}
	}
	return null;
}

/**
 * Get all permissions that can grant access to this path (primary + alternates).
 */
export function getAllPermissionsForPath(pathname: string): string[] {
	const normalized = pathname.replace(/\?.*$/, '');
	for (const entry of PATH_PERMISSION_ENTRIES) {
		if (pathMatches(entry.pattern, normalized)) {
			const perms = [entry.permission, ...(entry.alternatePermissions ?? [])];
			return perms;
		}
	}
	return [];
}

/**
 * Returns the first route the user is allowed to access (for redirect when current path is not allowed).
 */
const COURSE_BUILDER_PERMISSIONS = [PERMISSIONS.COURSES_LIST, PERMISSIONS.LABS_LIST, PERMISSIONS.EQUIPMENTS_LIST];
const CALENDAR_PERMISSIONS = [PERMISSIONS.ANNUAL_CALENDAR_VIEW, PERMISSIONS.ANNUAL_CALENDAR_LIST];
const APPEALS_PERMISSIONS = [PERMISSIONS.APPLICATION_APPEAL_VIEW, PERMISSIONS.APPLICATION_APPEAL_PENDING_LIST];
const CERTIFICATES_PERMISSIONS = [PERMISSIONS.CERTIFICATES_VIEW, PERMISSIONS.CERTIFICATES_LIST];
const TRACEABILITY_PERMISSIONS = [PERMISSIONS.TRACEABILITY_LIST, PERMISSIONS.TRACEABILITY_VIEW];
const MANAGE_SESSIONS_PERMISSIONS = [PERMISSIONS.MANAGE_SESSIONS_LIST, PERMISSIONS.MANAGE_SESSIONS_VIEW];
const MANAGE_USERS_PERMISSIONS = [PERMISSIONS.MANAGE_USERS_LIST, PERMISSIONS.MANAGE_USERS_VIEW];
const APPRENTICESHIP_INDUSTRY_PERMISSIONS = [
	PERMISSIONS.APPRENTICESHIP_INDUSTRY_LIST,
	PERMISSIONS.APPRENTICESHIP_INDUSTRY_VIEW,
];
const APPRENTICESHIP_APPRENTICE_PERMISSIONS = [
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_LIST,
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_VIEW,
];
const APPRENTICESHIP_OUTCOMES_PERMISSIONS = [
	PERMISSIONS.APPRENTICESHIP_OUTCOMES_LIST,
	PERMISSIONS.APPRENTICESHIP_OUTCOMES_VIEW,
	/** Allow opening /apprenticeship/outcomes when user has apprentice access (same as sidebar third item). */
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_LIST,
	PERMISSIONS.APPRENTICESHIP_APPRENTICE_VIEW,
];
const ROLES_AND_PERMISSIONS_PERMISSIONS = [PERMISSIONS.ROLES_LIST, PERMISSIONS.ROLES_VIEW];
const APPLICATIONS_INSPECTED_PERMISSIONS = [
	PERMISSIONS.APPLICATIONS_INSPECTED_LIST,
	PERMISSIONS.APPLICATIONS_VERIFIED_LIST,
	PERMISSIONS.APPLICATIONS_APPROVED_LIST,
	PERMISSIONS.ISSUE_CERTIFICATE,
];
const COMMITTEES_MY_PERMISSIONS = [
	PERMISSIONS.MY_COMMITTEE_PENDING_APPROVAL_LIST,
	PERMISSIONS.MY_COMMITTEE_CONSTITUTED_LIST,
	PERMISSIONS.MY_COMMITTEE_APPROVED_LIST,
	PERMISSIONS.MY_COMMITTEE_REJECTED_LIST,
];
const COMMITTEES_HISTORY_PERMISSIONS = [
	PERMISSIONS.HISTORY_CONSTITUTED_LIST,
	PERMISSIONS.HISTORY_APPROVED_LIST,
	PERMISSIONS.HISTORY_REJECT_LIST,
];
const CONFIGURATION_ROUTES_BY_PERMISSIONS = [
	[BROWSER_ROUTES.CONFIGURATION_COURSE_STREAM, COURSE_STREAM_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_ORGANIZATION_TYPE, ORGANIZATION_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_COURSE_CONTENT, COURSE_CONTENT_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_FACULTY_QUALIFICATIONS, QUALIFICATION_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_ATTACHMENT_TYPE, ATTACHMENT_TYPE_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_ATTACHMENT_OPTION, ATTACHMENT_OPTION_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_INSTITUTE_FACILITY, INSTITUTE_FACILITY_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_COMPETENCY_POINTS, COMPETENCY_POINT_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_SECTORS, SECTOR_CONFIGURATION_PERMISSIONS],
	[BROWSER_ROUTES.CONFIGURATION_COURSE_SCHEME, COURSE_SCHEME_CONFIGURATION_PERMISSIONS],
] as const;

export function userHasHomeDashboardAccess(
	userPermissions: string[],
	context: PsdaAdminDashboardAccessContext = {},
): boolean {
	return (
		permissionListIncludesPsdaAdminDashboard(userPermissions, context) ||
		permissionListIncludes(userPermissions, PERMISSIONS.CHAIRMAN_PBTE_DASHBOARD) ||
		EXECUTIVE_DASHBOARD_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p))
	);
}

/** True when org type is still loading and may change PSDA admin dashboard eligibility. */
export function dashboardAccessPendingProfile(
	userPermissions: string[],
	context: PsdaAdminDashboardAccessContext = {},
): boolean {
	if (!permissionListIncludes(userPermissions, PERMISSIONS.PSDA_ADMIN_DASHBOARD)) {
		return false;
	}
	if (context.organizationTypeId != null) {
		return false;
	}
	const entity = resolveEventEntity(context);
	return entity !== EVENT_ENTITY_PSDA && entity !== EVENT_ENTITY_PVTC && entity !== EVENT_ENTITY_TEVTA;
}

function routeIsAllowed(
	path: string,
	permission: string,
	userPermissions: string[],
	hasAllRead: boolean,
	context: PsdaAdminDashboardAccessContext,
): boolean {
	if (path === BROWSER_ROUTES.DASHBOARD) {
		return hasAllRead || userHasHomeDashboardAccess(userPermissions, context);
	}
	if (path === BROWSER_ROUTES.COURSE_BUILDER) {
		return hasAllRead || COURSE_BUILDER_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.CALENDAR) {
		return hasAllRead || CALENDAR_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (
		resolveEventManagementFirstAllowedRoute(path, userPermissions, hasAllRead, permissionListIncludes, {
			roles: context.roles,
			organizationTypeId: context.organizationTypeId,
			userTypeName: context.userTypeName,
		})
	) {
		return true;
	}
	if (resolveStaticCourseAssignFirstAllowedRoute(path, userPermissions, hasAllRead, permissionListIncludes)) {
		return true;
	}
	if (path === BROWSER_ROUTES.APPEALS) {
		return hasAllRead || APPEALS_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.CERTIFICATES) {
		return hasAllRead || CERTIFICATES_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.TRACEABILITY) {
		return hasAllRead || TRACEABILITY_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.MANAGE_SESSIONS) {
		return hasAllRead || MANAGE_SESSIONS_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.APPRENTICESHIP_STATISTICS) {
		return hasAllRead || permissionListIncludes(userPermissions, PERMISSIONS.APPRENTICESHIP_DASHBOARD_SUMMARY);
	}
	if (path === BROWSER_ROUTES.APPRENTICESHIP_OUTCOMES) {
		return hasAllRead || APPRENTICESHIP_OUTCOMES_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.APPRENTICESHIP_APPRENTICES) {
		return hasAllRead || APPRENTICESHIP_APPRENTICE_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.APPRENTICESHIP_INDUSTRY) {
		return hasAllRead || APPRENTICESHIP_INDUSTRY_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.MANAGE_USERS) {
		return hasAllRead || MANAGE_USERS_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.ROLES_AND_PERMISSIONS) {
		return hasAllRead || ROLES_AND_PERMISSIONS_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.APPLICATIONS_INSPECTED) {
		return hasAllRead || APPLICATIONS_INSPECTED_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.COMMITTEES_MY) {
		return hasAllRead || COMMITTEES_MY_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.COMMITTEES_HISTORY) {
		return hasAllRead || COMMITTEES_HISTORY_PERMISSIONS.some(p => permissionListIncludes(userPermissions, p));
	}
	if (path === BROWSER_ROUTES.CONFIGURATION) {
		if (hasAllRead) return true;
		return CONFIGURATION_ROUTES_BY_PERMISSIONS.some(([, permissions]) =>
			permissions.some(p => permissionListIncludes(userPermissions, p)),
		);
	}
	if (path === BROWSER_ROUTES.EMPLOYEE_ENDORSEMENT) {
		return permissionListIncludes(userPermissions, PERMISSIONS.EMPLOYER_LIST);
	}
	return hasAllRead || permissionListIncludes(userPermissions, permission);
}

export function getFirstAllowedRoute(
	userPermissions: string[],
	context: PsdaAdminDashboardAccessContext = {},
): string | null {
	const hasAllRead = permissionListIncludes(userPermissions, PERMISSIONS.ALL_READ);
	for (const [path, permission] of ORDERED_ROUTE_PERMISSIONS) {
		if (path === BROWSER_ROUTES.CONFIGURATION) {
			if (hasAllRead) return BROWSER_ROUTES.CONFIGURATION_COURSE_STREAM;
			const allowedModule = CONFIGURATION_ROUTES_BY_PERMISSIONS.find(([, permissions]) =>
				permissions.some(p => permissionListIncludes(userPermissions, p)),
			);
			if (allowedModule) return allowedModule[0];
			continue;
		}
		if (routeIsAllowed(path, permission, userPermissions, hasAllRead, context)) {
			return path;
		}
	}
	return null;
}

/** True when the user can open at least one sidebar route (excluding the no-access home shell). */
export function userHasAnyAllowedRoute(
	userPermissions: string[],
	context: PsdaAdminDashboardAccessContext = {},
): boolean {
	return getFirstAllowedRoute(userPermissions, context) !== null;
}

/**
 * Allow `/` with an empty-state when the user has no home dashboard and no other routes
 * (e.g. PVTC/TEVTA/Chairman with only mis-assigned `psda.admin.dashboard`).
 */
export function shouldShowDashboardNoAccessShell(
	userPermissions: string[],
	context: PsdaAdminDashboardAccessContext = {},
): boolean {
	if (permissionListIncludes(userPermissions, PERMISSIONS.ALL_READ)) {
		return false;
	}
	return (
		!userHasHomeDashboardAccess(userPermissions, context) &&
		!userHasAnyAllowedRoute(userPermissions, context)
	);
}

/**
 * Check if the path is a known protected path (exists in our permission list).
 * If false, the path should not be accessible (redirect).
 */
export function isPathInPermissionList(pathname: string): boolean {
	return getRequiredPermissionForPath(pathname) !== null;
}

/**
 * Check if the user has permission to access the given path (e.g. for post-login redirect).
 */
export function hasPermissionForPath(
	pathname: string,
	userPermissions: string[],
	context: PsdaAdminDashboardAccessContext = {},
): boolean {
	const normalized = pathname.replace(/\?.*$/, '');
	if (normalized === BROWSER_ROUTES.DASHBOARD || normalized === '/') {
		return userHasHomeDashboardAccess(userPermissions, context);
	}
	const entry = findPathPermissionEntry(pathname);
	if (!entry) return false;
	const perms = [entry.permission, ...(entry.alternatePermissions ?? [])];
	const allowAllRead = !entry.requireExplicitPermission;
	if (allowAllRead && permissionListIncludes(userPermissions, PERMISSIONS.ALL_READ)) return true;
	return perms.some(p => permissionListIncludes(userPermissions, p));
}

/**
 * Navigate to the dashboard/home page
 * @param navigate - React Router's navigate function
 */
export const goToHomePage = (navigate: NavigateFunction): void => {
  navigate(BROWSER_ROUTES.DASHBOARD);
}; 